/**
 * 스프레드시트 문서 → 엑셀(.xlsx) 내보내기.
 *
 * 업로드 경로(excelImport.ts)가 서식/수식을 버리고 셀 텍스트만 보관하므로, 내보내기도
 * 보관된 데이터(헤더 + 셀)로 .xlsx 를 재생성한다(round-trip). xlsx 는 lazy import.
 */
import type { SpreadsheetDocument } from '../../models/SpreadsheetDocument';

/** 일괄 다운로드한 파일 수와 제외된 문서의 사유. */
export interface SpreadsheetBatchExportResult {
  fileCount: number;
  issues: { name: string; message: string }[];
}

type XLSXModule = typeof import('xlsx');

let xlsxPromise: Promise<XLSXModule> | null = null;
function loadXLSX(): Promise<XLSXModule> {
  if (!xlsxPromise) xlsxPromise = import('xlsx');
  return xlsxPromise;
}

/** 문서를 AOA(2차원 배열: 헤더행 + 데이터행)로 평탄화. 순수 함수. */
export function documentToAoa(doc: SpreadsheetDocument): string[][] {
  const header = doc.columns.map((c) => c.name);
  const body = doc.rows.map((r) => doc.columns.map((c) => r.cells[c.id] ?? ''));
  return [header, ...body];
}

const INVALID_SHEET_CHARS = /[[\]:*?/\\]/g;
const EXCEL_SHEET_NAME_MAX = 31;

/** 엑셀 시트명 제약(31자, []:*?/\ 금지)에 맞춰 정규화. 빈 값은 'Sheet1'. */
export function sanitizeSheetName(name: string): string {
  const cleaned = name.replace(INVALID_SHEET_CHARS, ' ').trim().slice(0, EXCEL_SHEET_NAME_MAX);
  return cleaned || 'Sheet1';
}

/** 다운로드 파일명 안전화 — 경로 구분자/공백 등을 '_' 로, 확장자 중복 방지. 빈 값은 'document'. */
export function sanitizeFilename(name: string): string {
  const base = name
    .replace(/\.(xlsx|xls)$/i, '')
    .trim()
    .replace(/[\s\\/:*?"<>|-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  return base || 'document';
}

/**
 * 문서를 .xlsx 로 내려받는다.
 * @param doc 내보낼 문서
 * @param prefix 파일명 접두어(예: 현장명/카테고리) — 있으면 `prefix_제목.xlsx`.
 */
export async function exportDocumentToXlsx(
  doc: SpreadsheetDocument,
  prefix?: string,
): Promise<void> {
  const XLSX = await loadXLSX();
  const ws = XLSX.utils.aoa_to_sheet(documentToAoa(doc));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName(doc.name));
  const prefixPart = prefix ? `${sanitizeFilename(prefix)}_` : '';
  XLSX.writeFile(wb, `${prefixPart}${sanitizeFilename(doc.name)}.xlsx`);
}
